import { NavLink } from "react-router-dom";
import {
    TrendingUp,
    Store,
    Headphones,
    ShieldCheck,
    ClipboardCheck,
    Users,
    Wrench,
    Truck,
    Building2,
    Lock,
    Wallet,
    Scale,
    RefreshCw,
    DatabaseBackup,
    Package,
    Utensils,
    MessageSquareWarning,
    Activity,
    LayoutDashboard,
    Server,
    BarChart3,
    Cctv,
    ScanSearch,
    Link2,
    Trash2,
    Sparkles,
    Receipt,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Badge } from "@/components/ui/badge";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { OPS } from "@/constants/testIds";
import { BrandLogo } from "@/components/common/BrandLogo";

const ICONS = {
    TrendingUp, Store, Headphones, ShieldCheck, ClipboardCheck, Users, Wrench,
    Truck, Wallet, Scale, Package, Utensils, MessageSquareWarning, Server, Cctv,
    Trash2, Sparkles, Receipt,
};
// The two QC keys (qc_waste, qc_inspection) are merged into a single
// "Quality Management" entry that lives at /quality. See the render block
// below. Every other route now comes straight from the module registry
// (tab.route) — no hardcoded route map here.
const QC_KEYS = new Set(["qc_waste", "qc_inspection"]);

// HeroUI-inspired nav item: full-pill on active, subtle indent icon slot
const itemBase =
    "group relative flex items-center gap-3 rounded-full px-3.5 py-2 text-sm font-medium transition-all duration-150";

function ActiveNavItem({ to, icon: Icon, label, testId }) {
    return (
        <NavLink
            to={to}
            data-testid={testId}
            className={({ isActive }) =>
                cn(
                    itemBase,
                    isActive
                        ? "bg-[color:var(--brand-orange)] text-white shadow-[var(--shadow-sm)]"
                        : "text-[color:var(--ink-muted)] hover:bg-[color:var(--brand-orange-soft)] hover:text-[color:var(--ink)]"
                )
            }
        >
            {({ isActive }) => (
                <>
                    {Icon ? (
                        <Icon
                            className={cn(
                                "h-[18px] w-[18px] shrink-0 transition-colors duration-150",
                                isActive ? "text-white" : "text-[color:var(--brand-brown)]"
                            )}
                        />
                    ) : null}
                    <span className="truncate">{label}</span>
                </>
            )}
        </NavLink>
    );
}

function DisabledNavItem({ icon: Icon, label, tip }) {
    const { t } = useI18n();
    return (
        <TooltipProvider delayDuration={150}>
            <Tooltip>
                <TooltipTrigger asChild>
                    <div
                        className={cn(itemBase, "opacity-60 cursor-not-allowed")}
                        aria-disabled="true"
                    >
                        {Icon ? <Icon className="h-[18px] w-[18px] text-[color:var(--ink-muted)] shrink-0" /> : null}
                        <span className="truncate">{label}</span>
                        <Badge
                            data-testid={OPS.shell.comingSoonBadge}
                            variant="secondary"
                            className="ms-auto text-[10px] px-2 py-0"
                        >
                            {t("coming_soon")}
                        </Badge>
                    </div>
                </TooltipTrigger>
                <TooltipContent side="bottom">{tip}</TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
}

export function SidebarNav({ onNavigate }) {
    const { me, isSuperAdmin, isUserAdmin, isSystemAdmin, isBranchManager, canOpsDashboard, canBranchCheckin, canBranchKpi, canCctvManage, canQcViolationManage } = useAuth();
    const { t, tn } = useI18n();
    const nav = me?.nav || [];
    const canDashboard = canOpsDashboard;
    const canCheckin = canBranchCheckin;
    const canKpi = canBranchKpi;

    return (
        <div className="flex h-full flex-col" data-testid={OPS.shell.sidebar}>
            {/* Brand header — soft cream tile with a bit of extra breathing room */}
            <div className="flex items-center gap-3 px-4 h-16 border-b border-[color:var(--border-soft)]">
                <BrandLogo className="h-10 w-10 object-contain shrink-0" />
                <div className="min-w-0">
                    <p className="text-sm font-bold leading-tight text-[color:var(--ink)] truncate">
                        {t("app_name")}
                    </p>
                    <p className="text-[11px] text-[color:var(--ink-muted)] truncate">{t("company")}</p>
                </div>
            </div>

            {/* Nav */}
            <nav className="flex-1 overflow-y-auto scrollbar-thin px-3 py-4 space-y-1" onClick={onNavigate}>
                {canDashboard ? (
                    <ActiveNavItem
                        to="/dashboard"
                        icon={LayoutDashboard}
                        label={t("nav_dashboard")}
                        testId={OPS.opsDashboard.nav}
                    />
                ) : null}
                {canKpi ? (
                    <ActiveNavItem
                        to="/branch-kpi"
                        icon={BarChart3}
                        label={t("nav_branch_kpi")}
                        testId={OPS.branchKpi.nav}
                    />
                ) : null}
                {(() => {
                    const rendered = [];
                    let qualityInserted = false;
                    const qualityPermitted = nav.some(
                        (tab) => QC_KEYS.has(tab.key) && tab.status === "active" && tab.permitted
                    ) || canQcViolationManage;

                    nav.filter((tab) => tab.status !== "coming_soon").forEach((tab) => {
                        // Merge both QC keys into ONE "Quality Management" pill,
                        // rendered at the position of the FIRST QC entry.
                        if (QC_KEYS.has(tab.key)) {
                            if (!qualityInserted && qualityPermitted) {
                                rendered.push(
                                    <ActiveNavItem
                                        key="quality-hub"
                                        to="/quality"
                                        icon={ShieldCheck}
                                        label={t("quality_hub_nav")}
                                        testId={OPS.quality.nav}
                                    />
                                );
                                qualityInserted = true;
                            }
                            // Suppress the individual qc_waste / qc_inspection items.
                            return;
                        }
                        // CCTV Monitoring now hosts the Branch Check-in sub-tab.
                        // Show the pill when the user can access CCTV OR holds
                        // branch check-in authority (a "CCTV employee").
                        if (tab.key === "cctv_monitoring") {
                            if (tab.status === "active" && (tab.permitted || canCheckin || canCctvManage)) {
                                rendered.push(
                                    <ActiveNavItem
                                        key={tab.key}
                                        to={tab.route || "/cctv"}
                                        icon={ICONS[tab.icon] || Cctv}
                                        label={tn(tab)}
                                        testId={OPS.shell.navItem(tab.key)}
                                    />
                                );
                            }
                            return;
                        }
                        const Icon = ICONS[tab.icon] || TrendingUp;
                        if (tab.status === "active" && tab.permitted) {
                            rendered.push(
                                <ActiveNavItem
                                    key={tab.key}
                                    to={tab.route || "/"}
                                    icon={Icon}
                                    label={tn(tab)}
                                    testId={OPS.shell.navItem(tab.key)}
                                />
                            );
                        } else if (tab.status === "coming_soon") {
                            rendered.push(
                                <DisabledNavItem
                                    key={tab.key}
                                    icon={Icon}
                                    label={tn(tab)}
                                    tip={t("coming_soon_tip")}
                                />
                            );
                        }
                    });
                    return rendered;
                })()}

                {isUserAdmin ? (
                    <div className="pt-5">
                        <div className="mx-3 mb-2 flex items-center gap-2">
                            <span className="h-px flex-1 bg-[color:var(--separator)]" />
                            <p className="text-[10px] font-semibold uppercase tracking-wider text-[color:var(--ink-faint)]">
                                {t("nav_administration")}
                            </p>
                            <span className="h-px flex-1 bg-[color:var(--separator)]" />
                        </div>
                        <div className="space-y-1">
                            <ActiveNavItem
                                to="/admin/users"
                                icon={Users}
                                label={t("nav_users")}
                                testId={OPS.shell.navItem("users")}
                            />
                        </div>
                    </div>
                ) : null}

                {/* Infrastructure — Branches, Sales Sync, Ingest Status, Backups.
                    Gated by the `infrastructure` capability (super_admin holds it). */}
                {isSystemAdmin ? (
                    <div className="pt-5">
                        <div className="mx-3 mb-2 flex items-center gap-2">
                            <span className="h-px flex-1 bg-[color:var(--separator)]" />
                            <p className="text-[10px] font-semibold uppercase tracking-wider text-[color:var(--ink-faint)]">
                                {t("nav_infrastructure")}
                            </p>
                            <span className="h-px flex-1 bg-[color:var(--separator)]" />
                        </div>
                        <div className="space-y-1">
                            <ActiveNavItem
                                to="/admin/branches"
                                icon={Building2}
                                label={t("nav_branches")}
                                testId={OPS.shell.navItem("branches")}
                            />
                            <ActiveNavItem
                                to="/admin/sales-sync"
                                icon={RefreshCw}
                                label={t("nav_sales_sync")}
                                testId={OPS.shell.navItem("sales-sync")}
                            />
                            <ActiveNavItem
                                to="/admin/ingest-status"
                                icon={Activity}
                                label={t("nav_ingest_status")}
                                testId={OPS.system.nav}
                            />
                            <ActiveNavItem
                                to="/admin/backups"
                                icon={DatabaseBackup}
                                label={t("nav_backups")}
                                testId={OPS.shell.navItem("backups")}
                            />
                            {isSuperAdmin ? (
                                <>
                                    <ActiveNavItem
                                        to="/admin/hr-integration"
                                        icon={Link2}
                                        label={t("nav_hr_integration")}
                                        testId={OPS.shell.navItem("hr-integration")}
                                    />
                                    <ActiveNavItem
                                        to="/admin/data-audit"
                                        icon={ScanSearch}
                                        label={t("nav_data_audit")}
                                        testId={OPS.shell.navItem("data-audit")}
                                    />
                                </>
                            ) : null}
                        </div>
                    </div>
                ) : null}
            </nav>

            <div className="px-4 py-3 border-t border-[color:var(--border-soft)] flex items-center gap-2 text-[11px] text-[color:var(--ink-faint)]">
                <Lock className="h-3.5 w-3.5" />
                <span>ops.shallal.me</span>
            </div>
        </div>
    );
}
